import { useState } from "react";
import { adminGenerateSlots } from "../../lib/hooks";

function getNextMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

interface DaySchedule {
  start: number;
  end: number;
}

const DAY_LABELS = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
const DEFAULT_DAY: DaySchedule = { start: 8, end: 22 };

const HOUR_OPTIONS = Array.from({ length: 19 }, (_, i) => i + 6); // 6:00 – 24:00

function hourLabel(h: number): string {
  return String(h).padStart(2, "0") + ":00";
}

interface ResourceState {
  capacity: number;
  price: number;
}

interface GenerateSlotsProps {
  onGenerated?: () => void;
}

export function GenerateSlots({ onGenerated }: GenerateSlotsProps = {}) {
  const [weekStart, setWeekStart] = useState(getNextMonday);
  const [days, setDays] = useState<DaySchedule[]>(() => DAY_LABELS.map(() => ({ ...DEFAULT_DAY })));
  const [stolConfig, setStolConfig] = useState<ResourceState>({ capacity: 8, price: 10 });
  const [kruhConfig, setKruhConfig] = useState<ResourceState>({ capacity: 2, price: 10 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number; blockedByWorkshop: number } | null>(null);
  const [error, setError] = useState("");

  const slotCount = (s: DaySchedule) => Math.max(0, s.end - s.start);
  const totalSlots = days.reduce((sum, d) => sum + slotCount(d), 0) * 2;

  const setDay = (index: number, schedule: DaySchedule) => {
    setDays((prev) => prev.map((d, i) => (i === index ? schedule : d)));
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError("");
    setResult(null);

    const { data, error: err } = await adminGenerateSlots(
      weekStart,
      days,
      [
        { resourceType: "stol", capacity: stolConfig.capacity, priceCents: stolConfig.price * 100 },
        { resourceType: "kruh", capacity: kruhConfig.capacity, priceCents: kruhConfig.price * 100 },
      ]
    );
    if (err) {
      setError(err.message);
    } else if (data) {
      setResult({ created: data.created, skipped: data.skipped, blockedByWorkshop: data.blocked_by_workshop ?? 0 });
      if (data.created > 0) onGenerated?.();
    }
    setLoading(false);
  };

  const renderScheduleRow = (label: string, index: number) => {
    const schedule = days[index];
    return (
      <tr key={label}>
        <td className="schedule-label">{label}</td>
        <td>
          <select
            value={schedule.start}
            onChange={(e) => {
              const v = Number(e.target.value);
              setDay(index, v === 0 ? { start: 0, end: 0 } : { start: v, end: Math.max(schedule.end, v + 1) });
            }}
            className="schedule-select"
          >
            <option value={0}>Zatvorené</option>
            {HOUR_OPTIONS.map((h) => (
              <option key={h} value={h}>{hourLabel(h)}</option>
            ))}
          </select>
        </td>
        <td className="schedule-dash">–</td>
        <td>
          <select
            value={schedule.end}
            onChange={(e) => setDay(index, { ...schedule, end: Number(e.target.value) })}
            className="schedule-select"
            disabled={schedule.start === 0}
          >
            {HOUR_OPTIONS.filter((h) => h > schedule.start).map((h) => (
              <option key={h} value={h}>{hourLabel(h)}</option>
            ))}
          </select>
        </td>
        <td className="schedule-count">
          {schedule.start === 0 ? "zatvorené" : `${slotCount(schedule)} slotov / typ`}
        </td>
      </tr>
    );
  };

  const renderResourceRow = (
    label: string,
    config: ResourceState,
    setConfig: (c: ResourceState) => void,
  ) => (
    <div className="resource-row" key={label}>
      <span className="resource-label">{label}:</span>
      <label>
        kapacita
        <input type="number" min={1} className="resource-input" value={config.capacity}
          onChange={(e) => setConfig({ ...config, capacity: Math.max(1, Number(e.target.value)) })} />
      </label>
      <label>
        cena / hod
        <input type="number" min={0} className="resource-input" value={config.price}
          onChange={(e) => setConfig({ ...config, price: Math.max(0, Number(e.target.value)) })} />
        €
      </label>
    </div>
  );

  return (
    <div className="admin-card">
      <h3>Generovať sloty na týždeň</h3>

      <div className="form-field">
        <label>Týždeň od (pondelok):</label>
        <input
          type="date"
          value={weekStart}
          onChange={(e) => setWeekStart(e.target.value)}
        />
      </div>

      <div className="admin-schedule-info">
        <h4>Rozvrh:</h4>
        <table className="schedule-table">
          <tbody>
            {DAY_LABELS.map((label, i) => renderScheduleRow(label, i))}
          </tbody>
        </table>
        <div className="schedule-types">
          {renderResourceRow("Stôl", stolConfig, setStolConfig)}
          {renderResourceRow("Kruh", kruhConfig, setKruhConfig)}
        </div>
      </div>

      <div className="admin-warning">
        Existujúce sloty v tomto týždni nebudú prepísané.
      </div>

      <button
        className="btn btn-primary"
        onClick={handleGenerate}
        disabled={loading || !weekStart || totalSlots === 0}
      >
        {loading ? "Generujem..." : `Generovať ${totalSlots} slotov (7 dní × 2 typy)`}
      </button>

      {error && <div className="form-error">{error}</div>}
      {result && (
        <div className={`admin-result ${result.skipped > 0 ? "warning" : "success"}`}>
          {result.created > 0 && <span>Vytvorených {result.created} slotov</span>}
          {result.skipped > 0 && <span>{result.skipped} slotov už existuje</span>}
          {result.blockedByWorkshop > 0 && <span>{result.blockedByWorkshop} slotov zablokovaných existujúcim workshopom</span>}
          {result.created === 0 && result.skipped > 0 && <span>Všetky sloty už existujú.</span>}
        </div>
      )}
    </div>
  );
}
